import React from 'react';
import { Eye } from 'lucide-react';
import Button from './ui/Button';
import { Popover, PopoverContent, PopoverDescription, PopoverTitle, PopoverTrigger } from './pilot-ui/popover';
import { Table, TableBody, TableCell, TableRow } from './pilot-ui/table';
import { FilterLifeCalculation, FilterLifeDay } from '../types';
import { FilterReplacementStatus, filterReplacementThreshold } from '../utils/filterLifeDisplay';
import { formatCalendarDate } from '../utils/calendarDate';

/**
 * FS-1131: the eye beside Filter Life on the Analyze page, and the popup it
 * opens with every input behind the number: the start value, the highest
 * day's pressure (the day that set the floor the app shows), the end-of-life
 * limit, the formula with those numbers in it, the latest measured day, and the
 * change point. Both days are days the top speed was measured; a held day
 * repeats an earlier pressure and is never named (FS-1221). The formula is
 * worked on the highest day because the analysis holds every later day at
 * that day's value until a higher pressure is measured (FS-1148).
 */
interface FilterLifeCalculationPopoverProps {
  calculation: FilterLifeCalculation;
  /** The Firestore floor the app shows (airflowPercentageFloor). */
  floor: number | null | undefined;
  /** The analysis's display name, when the response lists it. */
  analysisName: string | null;
  thickness: number | null;
  predictedChangeDate: string | null | undefined;
  replacementStatus: FilterReplacementStatus;
}

// Stored percentages are rounded to one decimal place (filter_life_pct), so
// two values that agree to that place are the same number.
const PCT_MATCH_TOLERANCE = 0.05;

const START_SOURCE_TEXT: Record<string, string> = {
  measured: 'Measured on the new filter’s first good day',
  measured_used: 'Measured on the first good day of a filter already in use',
  assumed: 'Assumed from the used-filter table (MERV × thickness)',
  assumed_merv: 'Assumed from the used-filter table (MERV × thickness)',
  default: 'From the new-filter table, no longer used for new filters',
  default_merv: 'From the new-filter table, no longer used for new filters',
};

const pa = (value: number): string => `${value.toFixed(2)} Pa`;
const pct = (value: number): string => `${value.toFixed(1)}%`;
const samePct = (a: number, b: number): boolean => Math.abs(a - b) < PCT_MATCH_TOLERANCE;

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <TableRow>
    <TableCell className="w-28 align-top whitespace-normal text-muted-foreground">{label}</TableCell>
    <TableCell className="whitespace-normal">{children}</TableCell>
  </TableRow>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs text-muted-foreground">{children}</div>
);

const dayText = (day: FilterLifeDay): string =>
  day.pressure_pa != null
    ? `${pa(day.pressure_pa)} on ${formatCalendarDate(day.date)}`
    : formatCalendarDate(day.date);

const minutesAtPressureText = (minutes: number | null): string =>
  minutes == null ? '' : `, with ${Math.round(minutes)} minutes at that pressure`;

const FormulaRow: React.FC<{ calculation: FilterLifeCalculation; day: FilterLifeDay }> = ({
  calculation,
  day,
}) => {
  const start = calculation.start_pa;
  const limit = calculation.end_of_life_pa;
  if (start == null || day.pressure_pa == null || day.computed_pct == null) return null;
  return (
    <Row label="Formula">
      <div className="font-mono text-xs">
        (1 − ({day.pressure_pa.toFixed(2)} − {start.toFixed(2)}) ÷ ({limit.toFixed(2)} − {start.toFixed(2)})) × 100 = {pct(day.computed_pct)}
      </div>
      <Note>Life left between the start value and the limit, kept between 0 and 100%.</Note>
      {!samePct(day.computed_pct, day.filter_life_pct) && (
        <Note>
          Stored for that day: {pct(day.filter_life_pct)}. The start value or the limit has
          changed since that day was processed.
        </Note>
      )}
    </Row>
  );
};

const LatestDayRow: React.FC<{ latest: FilterLifeDay; highest: FilterLifeDay }> = ({ latest, highest }) => (
  <Row label="Latest measured day">
    <div>{dayText(latest)}</div>
    {latest.date === highest.date ? (
      <Note>The latest measured day is also the highest day.</Note>
    ) : (
      <Note>
        Its own pressure works out to {pct(latest.computed_pct ?? latest.filter_life_pct)}. Filter life
        is held at the highest day's value, so a lower reading does not raise it.
      </Note>
    )}
  </Row>
);

const AppShowsRow: React.FC<{ floor: number | null | undefined; lowest: FilterLifeDay | null }> = ({
  floor,
  lowest,
}) => (
  <Row label="App shows">
    {floor == null ? (
      <div>No value yet</div>
    ) : (
      <>
        <div>{pct(floor)}, rounded to {Math.min(100, Math.round(floor))}%</div>
        <Note>The app shows the lowest value since the filter went in. It never rises.</Note>
        {lowest && !samePct(floor, lowest.filter_life_pct) && (
          <Note>No day on record for this filter has that value.</Note>
        )}
      </>
    )}
  </Row>
);

const ChangePointRow: React.FC<{
  calculation: FilterLifeCalculation;
  thickness: number | null;
  predictedChangeDate: string | null | undefined;
  replacementStatus: FilterReplacementStatus;
}> = ({ calculation, thickness, predictedChangeDate, replacementStatus }) => {
  const thicknessInches = thickness ?? 1.0;
  const alertText = calculation.notify_pa != null
    ? `The replacement alert fires at ${pa(calculation.notify_pa)}, set on this device.`
    : `The replacement alert fires at ${calculation.notice_pct}% left (${pa(calculation.notice_pa)}).`;
  return (
    <Row label="Change point">
      <div>
        “Change by” or “Change soon” at {filterReplacementThreshold(thicknessInches)}% or less for
        a {thicknessInches}-inch filter, or while a warning alert is open. “Change now” at 0%, or
        while an urgent alert is open.
      </div>
      <Note>{alertText}</Note>
      {replacementStatus !== 'normal' && (
        <Note>A replacement alert is open ({replacementStatus}).</Note>
      )}
      {predictedChangeDate && (
        <Note>Predicted change date: {formatCalendarDate(predictedChangeDate)}.</Note>
      )}
    </Row>
  );
};

const FilterLifeCalculationPopover: React.FC<FilterLifeCalculationPopoverProps> = ({
  calculation,
  floor,
  analysisName,
  thickness,
  predictedChangeDate,
  replacementStatus,
}) => {
  // The lowest day records the highest top pressure reached in the period;
  // later stored values hold there until a higher pressure is measured.
  const { latest, lowest: highest } = calculation;
  return (
    <Popover>
      <PopoverTrigger
        render={(
          <Button
            variant="icon"
            size="sm"
            aria-label="Show how Filter Life was calculated"
            title="Show how Filter Life was calculated"
          />
        )}
      >
        <Eye aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <PopoverTitle>How Filter Life was calculated</PopoverTitle>
        {analysisName && (
          <PopoverDescription>Calculated by {analysisName}, which writes filter life for this Clip.</PopoverDescription>
        )}
        <Table>
          <TableBody>
            <Row label="Start">
              {calculation.start_pa != null && calculation.start_date ? (
                <>
                  <div>
                    {pa(calculation.start_pa)} on {formatCalendarDate(calculation.start_date)}
                    {minutesAtPressureText(calculation.start_minutes)}
                  </div>
                  {calculation.start_minutes == null && (
                    <Note>Minutes at that pressure were not recorded for this day.</Note>
                  )}
                  {calculation.start_source && (
                    <Note>{START_SOURCE_TEXT[calculation.start_source] ?? calculation.start_source}</Note>
                  )}
                </>
              ) : (
                <div>No start value yet. The filter is calibrating until its first good day.</div>
              )}
            </Row>
            <Row label="Highest day">
              {highest ? (
                <>
                  <div>
                    {dayText(highest)}{minutesAtPressureText(highest.top_minutes)}; stored {pct(highest.filter_life_pct)}
                  </div>
                  {highest.top_minutes == null && (
                    <Note>Minutes at that pressure were not recorded for this day.</Note>
                  )}
                  <Note>
                    The highest top pressure measured since the filter went in, on a day the top level
                    ran at least {calculation.min_cluster_minutes} minutes (one reading a minute). Filter
                    life is read from this day and held there until a higher pressure is measured on such a day.
                  </Note>
                </>
              ) : (
                <div>No measured day yet</div>
              )}
            </Row>
            <Row label="Limit">
              <div>{pa(calculation.end_of_life_pa)}</div>
              <Note>
                {calculation.end_of_life_on_device
                  ? 'End of life, set on this device.'
                  : 'End of life, the fleet default.'}
              </Note>
            </Row>
            {highest && <FormulaRow calculation={calculation} day={highest} />}
            <AppShowsRow floor={floor} lowest={highest} />
            {latest && highest && <LatestDayRow latest={latest} highest={highest} />}
            <ChangePointRow
              calculation={calculation}
              thickness={thickness}
              predictedChangeDate={predictedChangeDate}
              replacementStatus={replacementStatus}
            />
          </TableBody>
        </Table>
      </PopoverContent>
    </Popover>
  );
};

export default FilterLifeCalculationPopover;
